// ============================================================
// REGENERATION, DRAWN (plan body-map T2; engine/plate/auras `regenLook`).
// A ring of green-gold light climbs the body from the feet to the head: its
// far half behind the figure, its near half before it, sparks running round
// it, and the lines inside its height re-drawn in its light (a copy of the
// lines under a clip that climbs with it). Motes drift up between rings; a
// faint copy lingers from 5. The climb is a scene the clock writes (ring,
// sparks and clip together); the sparks' orbit and the motes are tracks.
// ============================================================

import type { Box } from '../../../../engine/plate/frame';
import { regenAt, regenLook } from '../../../../engine/plate/auras';
import tx from '../../../../styles/PlateTraits.module.css';
import { tracked } from '../../usePlateClock';
import type { WrittenNode } from '../scene';

/** The moment the still ring shows: about a third of the way up. */
const STILL_T = 1.8;

interface Props {
  u: string;
  /** Regeneration (X). */
  x: number;
  box: Box;
}

/** One half of the ring: the far half arcs over (`sweep` 1), the near half under. */
function RingHalf({ x, box, sweep, opacity, u }: Props & { sweep: 0 | 1; opacity: number }) {
  const { cx, rx, ry, ring } = regenLook(x, box);
  const inner = rx * 0.94;
  return (
    <g opacity={opacity} filter={`url(#${u}-tglow)`}>
      <path className={tx.regenRing} d={`M${cx - rx} 0 A${rx} ${ry} 0 0 ${sweep} ${cx + rx} 0`} strokeWidth={ring} />
      <path
        className={tx.regenDash}
        d={`M${cx - inner} 0 A${inner} ${ry * 0.94} 0 0 ${sweep} ${cx + inner} 0`}
        strokeWidth={3}
      />
    </g>
  );
}

/** Where the ring stands at the still moment, for the climbing groups' first draw. */
const climbAt = (x: number, box: Box) => `translate(0 ${Math.round(regenAt(regenLook(x, box), STILL_T))})`;

/** Behind the figure: the ring's far half, dimmer. */
export function RegenBack({ u, x, box }: Props) {
  return (
    <g pointerEvents="none" data-trait="regeneration" data-regen={x}>
      <g data-g="rg-back" transform={climbAt(x, box)}>
        <RingHalf u={u} x={x} box={box} sweep={1} opacity={regenLook(x, box).opacity * 0.55} />
      </g>
    </g>
  );
}

/** Before the figure: the lines re-drawn inside the ring's height, its near half, its sparks, the motes. */
export function RegenFront({ u, x, box }: Props) {
  const look = regenLook(x, box);
  const [x0, , x1] = box;
  const y = regenAt(look, STILL_T);
  return (
    <g pointerEvents="none">
      <clipPath id={`${u}-rg`}>
        <rect data-g="rg-clip" x={x0 - 400} width={x1 - x0 + 800} y={y - look.ry * 1.3} height={look.ry * 2.6} />
      </clipPath>
      <g clipPath={`url(#${u}-rg)`} opacity={look.opacity}>
        <g className={tx.regenLines} style={{ strokeWidth: look.lineWidth }}>
          <use href={`#${u}-art`} />
        </g>
      </g>
      <g data-g="rg-front" transform={climbAt(x, box)}>
        <RingHalf u={u} x={x} box={box} sweep={0} opacity={look.opacity} />
        <g transform={`translate(${look.cx} 0)`}>
          {look.sparks.map(({ r, cx, cy }, k) => (
            <circle key={k} className={tx.regenSpark} r={r} filter={`url(#${u}-tglow)`} {...tracked([cx, cy])} />
          ))}
        </g>
      </g>
      {look.lingers && (
        <g className={tx.regenLines} opacity={0.12}>
          <use href={`#${u}-art`} />
        </g>
      )}
      {look.motes.map(({ cx, cy, opacity }, k) => (
        <circle key={k} className={tx.regenMote} cx={cx} r={14} {...tracked([cy, opacity])} />
      ))}
    </g>
  );
}

/** What the clock writes of the ring at `t`: its two halves and the clip, climbing together. */
export function regenNodes(x: number, box: Box, t: number): WrittenNode[] {
  const look = regenLook(x, box);
  const y = regenAt(look, t);
  const transform = `translate(0 ${Math.round(y)})`;
  return [
    { key: 'rg-back', attrs: { transform } },
    { key: 'rg-front', attrs: { transform } },
    { key: 'rg-clip', attrs: { y: Math.round(y - look.ry * 1.3) } },
  ];
}

/** The ring at the still moment. */
export const regenStill = (x: number, box: Box) => regenNodes(x, box, STILL_T);
